import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowRight, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Basement Development', path: '/basements' },
    { name: 'Bathroom Renovation', path: '/bathrooms' },
    { name: 'Custom Features', path: '/custom-features' },
    { name: 'Legal Basement Suites', path: '/basements' },
    { name: 'Custom Showers', path: '/bathrooms' },
  ];

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
    { name: 'Free Estimate', path: '/contact' },
  ];

  return (
    <footer className="bg-[#0f172a] text-white" data-testid="main-footer">
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-2">
                Ready to Transform Your Space?
              </h2>
              <p className="text-slate-400 text-lg">
                Get a free, no-obligation estimate for your renovation project.
              </p>
            </div>
            <Link
              to="/contact"
              className="group flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
              data-testid="footer-cta-button"
            >
              Request Estimate
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <span className="font-heading text-2xl font-bold">YYC Custom</span>
              <span className="block text-xs uppercase tracking-[0.2em] text-slate-400 mt-1">
                Basement & Bath
              </span>
            </div>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Calgary's trusted experts for basement development and bathroom renovations.
              Built Right. Built to Last.
            </p>
            <div className="space-y-3">
              <a
                href="tel:403-402-6596"
                className="flex items-center gap-3 text-slate-300 hover:text-[#c5a059] transition-colors"
                data-testid="footer-phone"
              >
                <Phone size={16} className="text-[#c5a059]" />
                <span>403-402-6596</span>
              </a>
              <a
                href="mailto:info@yyccustombasement.ca"
                className="flex items-center gap-3 text-slate-300 hover:text-[#c5a059] transition-colors"
                data-testid="footer-email"
              >
                <Mail size={16} className="text-[#c5a059]" />
                <span>info@yyccustombasement.ca</span>
              </a>
              <div className="flex items-center gap-3 text-slate-400">
                <MapPin size={16} className="text-[#c5a059]" />
                <span>Calgary, Alberta</span>
              </div>
              <a
                href="https://www.instagram.com/yyccustombasement/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex items-center gap-3 text-slate-300 hover:text-[#c5a059] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={16} className="text-[#c5a059]" />
                <span>@yyccustombasement</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="footer-link flex items-center gap-2"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight size={12} className="text-[#c5a059]" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="footer-link flex items-center gap-2"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight size={12} className="text-[#c5a059]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Service Areas</h3>
            <ul className="space-y-2 text-slate-400">
              <li>Calgary</li>
              <li>Airdrie</li>
              <li>Cochrane</li>
              <li>Chestermere</li>
              <li>Okotoks</li>
            </ul>
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-3 text-slate-400">
                <Clock size={16} className="text-[#c5a059]" />
                <div className="text-sm">
                  <p>Mon - Fri: 8am - 6pm</p>
                  <p>Sat: By Appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; {currentYear} YYC Custom Basement & Bath. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <span>Fully Licensed & Insured</span>
              <span className="hidden md:inline">|</span>
              <span>Local Calgary Contractor</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
